<template>
  <b-card class="auth-register-page shadow">
    <div class="mb-4 text-center">
      <h3>{{ $t('auth.register.title') }}</h3>
      <!-- eslint-disable-next-line vue-i18n/no-missing-keys -->
      <p v-if="$te('auth.register.description')">{{ $t('auth.register.description') }}</p>
    </div>

    <b-alert v-if="isDemoSite" class="mb-4" show variant="info">
      <strong>In the demo site</strong>, this form won't create a new account.
    </b-alert>

    <b-alert v-if="err" class="mb-4" show variant="danger">
      <template v-if="$findApolloError(err, 'extensions.code', 'EMAIL_DOMAIN_INVALID')">
        {{ $t('auth.errors.email-domain-invalid', { validDomains: err.extensions.domains.join(', ') }) }}
      </template>
      <template v-else>
        {{ (err.extensions ? err.extensions.message : null) || err.message || err }}
      </template>
    </b-alert>

    <form v-if="canAuthEmailPassword" method="POST" @submit.prevent="submitForm">
      <b-form-group>
        <b-form-input id="firstName" type="text" :placeholder="$t('auth.placeholders.firstName')" :state="formState('firstName')" v-model="form.firstName"/>
        <div v-if="typeof formValid.firstName === 'string'" class="invalid-feedback">
          <template v-if="formValid.firstName === 'EMPTY'">{{ $t('auth.validation.firstName-empty') }}</template>
          <template v-else-if="formValid.firstName === 'TOO_SHORT'">{{ $t('auth.validation.firstName-too-short') }}</template>
          <template v-else>{{ formValid.firstName }}</template>
        </div>
      </b-form-group>

      <b-form-group>
        <b-form-input id="lastName" type="text" :placeholder="$t('auth.placeholders.lastName')" :state="formState('lastName')" v-model="form.lastName"/>
        <div v-if="typeof formValid.lastName === 'string'" class="invalid-feedback">
          <template v-if="formValid.lastName === 'EMPTY'">{{ $t('auth.validation.lastName-empty') }}</template>
          <template v-else-if="formValid.lastName === 'TOO_SHORT'">{{ $t('auth.validation.lastName-too-short') }}</template>
          <template v-else>{{ formValid.lastName }}</template>
        </div>
      </b-form-group>

      <b-form-group>
        <b-input-group>
          <b-form-input autocomplete="email email-address" id="emailAddress" type="email" :placeholder="$t('auth.placeholders.emailAddress')"
            :disabled="Boolean(emailAddress)" required :state="formState('emailAddress')" v-model="form.emailAddress"/>
          <b-input-group-append v-if="emailAddress">
            <b-btn class="email-change-btn text-dark" @click="() => $emit('setEmailAddress', null)" tabindex="-1" variant="transparent">
              <font-awesome-icon icon="pencil-alt"/>
            </b-btn>
          </b-input-group-append>
        </b-input-group>
        <div v-if="typeof formValid.emailAddress === 'string'" class="invalid-feedback">
          <template v-if="formValid.emailAddress === 'EMPTY'">{{ $t('auth.validation.emailAddress-empty') }}</template>
          <template v-else-if="formValid.emailAddress === 'TOO_SHORT'">{{ $t('auth.validation.lastName-too-short') }}</template>
          <template v-else>{{ formValid.emailAddress }}</template>
        </div>
      </b-form-group>

      <b-form-group>
        <b-form-input id="password" type="password" :placeholder="$t('auth.placeholders.password')" :state="formState('password')" v-model="form.password"/>
        <b-form-text v-if="passwordPolicy">
          {{ $t('auth.password-policy.prefix') }}:
          <span v-if="passwordPolicy.requireUppercase" class="d-block ml-1"
            :class="{ 'text-success': passwordValid.requireUppercase === true, 'text-danger': passwordValid.requireUppercase === false }">
            • {{ $t('auth.password-policy.contains-uppercase') }}
          </span>
          <span v-if="passwordPolicy.requireLowercase" class="d-block ml-1"
            :class="{ 'text-success': passwordValid.requireLowercase === true, 'text-danger': passwordValid.requireLowercase === false }">
            • {{ $t('auth.password-policy.contains-lowercase') }}
          </span>
          <span v-if="passwordPolicy.requireNumbers" class="d-block ml-1"
            :class="{ 'text-success': passwordValid.requireNumbers === true, 'text-danger': passwordValid.requireNumbers === false }">
            • {{ $t('auth.password-policy.contains-number') }}
          </span>
          <span v-if="passwordPolicy.requireSymbols" class="d-block ml-1"
            :class="{ 'text-success': passwordValid.requireSymbols === true, 'text-danger': passwordValid.requireSymbols === false }">
            • {{ $t('auth.password-policy.contains-symbol') }} (^ $ * . [ ] { } ( ) ? " ! @ # % &amp; / \ , &gt; &lt; ' : ; | _ ~ `)
          </span>
          <span v-if="passwordPolicy.minimumLength" class="d-block ml-1"
            :class="{ 'text-success': passwordValid.minimumLength === true, 'text-danger': passwordValid.minimumLength === false }">
            • {{ $t('auth.password-policy.longer-than', { n: passwordPolicy.minimumLength }) }}
          </span>
        </b-form-text>
      </b-form-group>

      <b-form-group>
        <b-form-input id="passwordConfirmation" type="password" :placeholder="$t('auth.placeholders.passwordConfirm')" :state="formState('passwordConfirm')" v-model="form.passwordConfirm"/>
        <div v-if="typeof formValid.passwordConfirm === 'string'" class="invalid-feedback">
          <template v-if="formValid.passwordConfirm === 'EMPTY'">{{ $t('auth.validation.passwordConfirm-empty') }}</template>
          <template v-else-if="formValid.passwordConfirm === 'CONFIRM_MISMATCH'">{{ $t('auth.validation.passwordConfirm-mismatch') }}</template>
          <template v-else>{{ formValid.passwordConfirm }}</template>
        </div>
      </b-form-group>

      <b-form-group v-if="$root.isCommunitySite">
        <b-form-checkbox class="subtle-text" v-model="form.marketingRealLinks">
          I would like to hear from <a href="https://www.reallinks.io/?utm_source=real-links-prod&utm_medium=signup&utm_campaign=community"
            tabindex="-1" target="_blank">Real Links</a> about HR, Referrals &amp; other relevant content
        </b-form-checkbox>
        <b-form-checkbox class="subtle-text" v-model="form.marketingChatTalent">
          I would like to hear from <a href="https://www.chattalent.com/?utm_source=reallinks&utm_medium=signup&utm_campaign=community"
            tabindex="-1" target="_blank">#ChatTalent</a> about HR, Talent &amp; Recruitment industry content
        </b-form-checkbox>
        <p class="subtle-text m-0 ml-4">
          You can unsubscribe from either of these at any time.
        </p>
      </b-form-group>

      <b-form-group>
        <b-form-checkbox class="subtle-text" v-model="form.termsAccepted">
          <i18n path="auth.register.agree-to-terms">
            <template v-slot:termsConditionsLink>
              <a href="/static/documents/Real+Links+Terms+%26+Conditions.pdf" tabindex="-1" target="_blank">
                {{ $t('general.terms-conditions') }}
              </a>
            </template>
            <template v-slot:privacyPolicyLink>
              <a href="https://www.reallinks.io/privacy-policy/" tabindex="-1" target="_blank">
                {{ $t('general.privacy-policy') }}
              </a>
            </template>
          </i18n>
          <template v-if="isHelloFresh">
            <br /><br /> {{ $t('auth.register.agree-to-terms-additional') }}
          </template>
        </b-form-checkbox>
      </b-form-group>

      <b-form-group>
        <b-btn class="w-100" :disabled="!isFormValid || working" size="md" type="submit" variant="primary">
          <template v-if="working">{{ $t('auth.ctas.registering') }}</template>
          <template v-else>{{ $t('auth.ctas.register') }}</template>
        </b-btn>
      </b-form-group>
    </form>

    <p v-if="canAuthSingleSignOn && canAuthEmailPassword" class="text-center my-4 mb-1">OR</p>

    <form class="mb-4 text-center" v-if="canAuthSingleSignOn">
      <p v-if="!canAuthEmailPassword" class="mb-1">{{ $t('auth.single-sign-on.prompt') }}:</p>
      <template v-if="isDemoSite">
        <single-sign-on-button class="d-none" :provider="{}"/>
        <b-form-group class="w-100 text-center">
          <b-btn class="single-sign-on type-SAML name-microsoft" @click.prevent="submitForm" variant="transparent">
            <font-awesome-icon class="fa-icon" :icon="[ 'fab', 'windows' ]"/>
            {{ $t('auth.single-sign-on.sign-in-with-ext', { name: 'Microsoft' }) }}
          </b-btn>
          <i18n path="auth.single-sign-on.with-your-ext-identifiers" tag="b-form-text">
            <template v-slot:domains>
              <strong>{{ $tc('grammar.or-list', 1, [ 'reallinks.io' ]) }}</strong>
            </template>
          </i18n>
        </b-form-group>
        <b-form-group>
          <b-btn class="single-sign-on type-Google name-google" @click.prevent="submitForm" variant="transparent">
            <font-awesome-icon class="fa-icon" :icon="[ 'fab', 'google' ]"/>
            {{ $t('auth.single-sign-on.sign-in-with-ext', { name: 'Google' }) }}
          </b-btn>
          <i18n path="auth.single-sign-on.with-your-ext-identifiers" tag="b-form-text">
            <template v-slot:domains>
              <strong>{{ $tc('grammar.or-list', 1, [ 'reallinks.org' ]) }}</strong>
            </template>
          </i18n>
        </b-form-group>
      </template>
      <template v-else>
        <template v-for="(provider, i) in identityProviders" v-if="provider.type !== 'COGNITO'">
          <single-sign-on-button :provider="provider" :key="i"/>
        </template>
      </template>
    </form>

    <div v-if="canAuthEmailPassword && !emailAddress" class="d-flex flex-row justify-content-start align-items-center">
      <p class="mb-0"><router-link :to="{ name: 'Login', query: $route.query }">{{ $t('auth.ctas.reset-form') }}</router-link></p>
    </div>
  </b-card>
</template>

<script>
import AuthSignupByEmailMutation from '@src/graphql/mutations/AuthSignupByEmail.gql';
import authMixin from '@src/mixins/auth';
import formValidationMixin from '@src/mixins/formValidation';
import ls from 'local-storage';
import SingleSignOnButton from '@src/components/auth/SingleSignOnButton';
import { toDataURI } from '@src/lib/utils';
import { mapRoot } from '@src/mixins/root';

export default {
  props: {
    company: Object,
    emailAddress: {
      type: String,
      default: () => null,
    },
    userInviteID: {
      type: String,
      default: () => null,
    },
    userReferrerID: {
      type: String,
      default: () => null,
    },
  },
  components: {
    SingleSignOnButton,
  },
  mixins: [
    authMixin,
    formValidationMixin,
  ],
  data: () => ({
    err: null,
    form: {},
    working: false,
  }),
  created() {
    if (this.isDemoSite && this.isLoggedIn) {
      // If we're logged into the demo, then we're showing the register page
      this.form = {
        ...this.form,
        firstName: 'Riley',
        lastName: 'McFarland',
        emailAddress: 'riley.mcfarland@demo.reallinks.io',
        password: 'secure-password',
        passwordConfirm: 'secure-password',
        termsAccepted: true,
      };
    } else if (this.isDemoSite) {
      // If we're not logged into the demo, we need to
      this.$router.push({
        name: 'Login',
        query: { next: '/register' },
      });
    } else if (this.isLoggedIn) {
      // If we're just logged in, shouldn't have got here
      return this.afterAuthenticated();
    } else {
      const { userReferrer, userInvite } = this.company;

      if (this.emailAddress) {
        this.form.emailAddress = this.emailAddress;
      }

      if (userReferrer && userReferrer.id) {
        ls.set('referrerUserID', userReferrer.id);
      }

      if (userInvite && userInvite.id) {
        if (userInvite.name) {
          const [ firstName, ...lastName ] = (userInvite.name || '').split(' ');
          this.form = {
            firstName,
            lastName: lastName.join(' '),
          };
        }

        if (userInvite.emailAddress) {
          this.form.emailAddress = userInvite.emailAddress;
          this.$emit('setEmailAddress', userInvite.emailAddress);
        }
      }
    }
  },
  mounted() {
    const firstInput = this.$el.querySelector('input:first-child');
    firstInput && firstInput.focus();
    // window.alert(`${this.$route.query.redirect}`);
    // window.alert(`${this.redirectTo}`);
  },
  metaInfo() {
    return {
      title: this.$t('auth.register.title'),
    };
  },
  formValidation: {
    firstName(value) {
      if (value && value.length < 2) {
        return 'TOO_SHORT';
      } else if (!value) {
        return 'EMPTY';
      } else {
        return true;
      }
    },
    lastName(value) {
      if (value && value.length < 2) {
        return 'TOO_SHORT';
      } else if (!value) {
        return 'EMPTY';
      } else {
        return true;
      }
    },
    emailAddress(value) {
      if (value && value.length < 2) {
        return 'TOO_SHORT';
      } else if (!value) {
        return 'EMPTY';
      } else {
        return true;
      }
    },
    password(value) {
      if (typeof this.forcePasswordInvalid === 'boolean') {
        return this.forcePasswordInvalid;
      } else if (typeof value === 'string') {
        return this.passwordPolicy && this.passwordValid
          ? Object.keys(this.passwordValid).reduce((r, key) => r && (this.passwordValid[key] !== false), true)
          : true;
      } else {
        return null;
      }
    },
    passwordConfirm(value, { password }) {
      if (password && value !== password) {
        return 'CONFIRM_MISMATCH';
      } else if (!value) {
        return 'EMPTY';
      } else {
        return true;
      }
    },
    termsAccepted(value) {
      return value;
    },
  },
  computed: {
    ...mapRoot([ 'isLoggedIn', 'isDemoSite', 'isEventsSite', 'isHelloFresh' ]),
    isFormValid() {
      const invalidInputs = Object.keys(this.formValid).filter(key => this.formValid[key] !== true);
      if (invalidInputs.length) return false;
      return true;
    },
    passwordPolicy() {
      const { cognito: { passwordPolicy = {} } } = this.company || {};
      return passwordPolicy;
    },
    passwordValid() {
      const { cognito: { passwordPolicy } } = this.company || {};
      const { requireUppercase, requireLowercase, requireNumbers, requireSymbols, minimumLength } = passwordPolicy || {};
      const { password } = this.form || {};
      const hasPassword = typeof password === 'string';
      return {
        requireUppercase: requireUppercase && hasPassword ? /[A-Z]/g.test(password) : null,
        requireLowercase: requireLowercase && hasPassword ? /[a-z]/g.test(password) : null,
        requireNumbers: requireNumbers && hasPassword ? /[0-9]/g.test(password) : null,
        // eslint-disable-next-line no-useless-escape
        requireSymbols: requireSymbols && hasPassword ? /[\^$*.\[\]{}()?"!@#%&/,><':;|_~`]/g.test(password) : null,
        minimumLength: minimumLength && hasPassword ? password.length >= minimumLength : null,
      };
    },
  },
  methods: {
    formState(key) {
      return this.formValid[key] === true ? true : (typeof this.formValid[key] === 'string' ? false : null);
    },
    async submitForm() {
      this.$emit('setEmailAddress', this.form.emailAddress);
      this.working = true;

      try {
        const invalidInputs = Object.keys(this.formValid).reduce((results, key) => {
          if (this.formValid[key] !== true) {
            results.push(key);
            if (!this.form[key]) {
              this.form[key] = null;
            }
          }
          return results;
        }, []);

        if (invalidInputs.length) {
          this.form = this.form;
          throw new Error('One or more form fields requires your attention');
        }

        if (this.isDemoSite) {
          await this.afterSignup(this.nextRoute);
          return;
        }

        const referrerUserID = ls.get('referrerUserID');

        const { errors } = await this.$apollo.mutate({
          mutation: AuthSignupByEmailMutation,
          variables: {
            signup: {
              firstName: this.form.firstName,
              lastName: this.form.lastName,
              emailAddress: this.form.emailAddress,
              password: this.form.password ? toDataURI('text/plain;charset=utf8', this.form.password) : null,
              locale: this.$i18n.locale,
              referrerUser: referrerUserID ? { id: referrerUserID } : null,
              contextData: this.getCognitoContextData(this.form.emailAddress),
            },
          },
          update: (client, { data: { signupByEmail } }) => this.updateAuthResponse(client, signupByEmail),
          errorPolicy: 'all',
        });

        if (Array.isArray(errors)) {
          errors.forEach(err => this.$log.error('signupByEmail', err));
          throw errors.shift();
        }

        this.$assert(this.isLoggedIn, 401, new Error('Failed to signup by email'));

        // Always remove the referrerUserID from signup
        ls.remove('referrerUserID');

        this.$root.captureLog({
          eventName: 'USER_ACCEPTED_TERMS',
          user: { self: true },
        });

        if (this.$root.isCommunitySite && (this.form.marketingRealLinks || this.form.marketingChatTalent)) {
          this.$root.captureLog({
            eventName: 'USER_ACCEPTED_MARKETING',
            user: { self: true },
            data: [
              { key: 'marketingRealLinks', value: this.form.marketingRealLinks ? 'true' : 'false' },
              { key: 'marketingChatTalent', value: this.form.marketingChatTalent ? 'true' : 'false' },
            ],
          });
        }

        if (this.isEventsSite) {
          return this.$router.push(this.nextRoute || { name: 'Dashboard' });
        }

        await this.afterSignup(this.nextRoute);
      } catch (err) {
        this.$log.error(err);
        this.err = err;
        this.working = false;
      }
    },
  },
};
</script>

<style lang="scss">
.auth-register-page {
  .subtle-text {
    font-size: 12px;
    font-weight: bold;

    a {
      font-size: 12px;
      font-weight: bold;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Auth/Register.vue